export interface IAcademicsScheduleResponse {
  schedules: IAcademicsSchedule[];
  total: number;
}

export interface IAcademicsSchedule {
  id: string;
  name: string;
  description?: string;
  type: EScheduleType;
  academicYear: string;
  term?: ETerm;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  periods: ISchedulePeriod[];
  instituteId?: string;
  classId?: string;
}

// ============================================================
// API Request/Response Interfaces (matching backend API)
// ============================================================

export interface IScheduleRequest {
  schedule: {
    instituteId: string;
    name: string;
    classId: string;
    type: 'weekly' | 'daily' | 'custom';
    academicYear: string;
    description?: string;
    term?: 'first' | 'second' | 'third';
    isActive?: boolean;
  };
}

export interface IScheduleResponse {
  id: string;
  name: string;
  type: string;
  academicYear: string;
  term?: string;
  isActive: boolean;
  class?: {
    id: string;
    name?: string;
    section?: string;
  };
  institute: {
    id: string;
    name?: string;
  };
  periods?: IPeriodResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface IPeriodResponse {
  id: string;
  periodNumber: number;
  startTime: string;
  endTime: string;
  dayOfWeek: string;
  course?: {
    id: string;
    name?: string;
  };
  teacher?: {
    id: string;
    name?: string;
  };
  room?: string;
  isBreak?: boolean;
}

export interface IScheduleListQueryParams {
  page?: number;
  limit?: number;
  instituteId?: string;
  classId?: string;
  academicYear?: string;
  type?: string;
  isActive?: boolean;
}


export interface ISchedulePeriod {
  id: string;
  periodNumber: number;
  startTime: string; // HH:MM format
  endTime: string; // HH:MM format
  dayOfWeek: EWeekDay;
  courseId?: number; // References a course
  teacherId?: number; // References a teacher
  classId?: number; // References a class
  room?: string;
  isBreak?: boolean;
  breakType?: EBreakType;
}

export interface ITeacherSchedule {
  teacherId: number;
  teacherName: string;
  schedules: ITeacherScheduleItem[];
}

export interface ITeacherScheduleItem {
  day: EWeekDay;
  periods: ISchedulePeriod[];
  totalPeriods: number;
}

export interface IStudentSchedule {
  studentId: string;
  studentName: string;
  classId: number;
  className: string;
  section?: string;
  schedules: IStudentScheduleDay[];
}

export interface IStudentScheduleDay {
  day: EWeekDay;
  periods: ISchedulePeriod[];
}

export interface IClassSchedule {
  classId: number;
  className: string;
  section?: string;
  schedules: IClassScheduleDay[];
}

export interface IClassScheduleDay {
  day: EWeekDay;
  periods: ISchedulePeriod[];
}

// Composite view for teacher to see any class schedule
export interface ICompositeScheduleView {
  scheduleName: string;
  scheduleId: string;
  viewType: EScheduleViewType;
  referenceId: string | number; // Could be classId, teacherId, etc.
  referenceName: string; // Could be className, teacherName, etc.
  days: IScheduleDay[];
}

export interface IScheduleDay {
  day: EWeekDay;
  periods: ISchedulePeriod[];
}

// Enums
export enum EScheduleType {
  REGULAR = 'Regular',
  EXAM = 'Exam',
  SPECIAL_EVENT = 'Special Event',
  HOLIDAY = 'Holiday',
}

export enum ETerm {
  TERM_1 = 'Term 1',
  TERM_2 = 'Term 2',
  TERM_3 = 'Term 3',
  SEMESTER_1 = 'Semester 1',
  SEMESTER_2 = 'Semester 2',
  ANNUAL = 'Annual',
}

export enum EWeekDay {
  MONDAY = 'Monday',
  TUESDAY = 'Tuesday',
  WEDNESDAY = 'Wednesday',
  THURSDAY = 'Thursday',
  FRIDAY = 'Friday',
  SATURDAY = 'Saturday',
  SUNDAY = 'Sunday',
}

export enum EBreakType {
  LUNCH = 'Lunch',
  SHORT_BREAK = 'Short Break',
  ASSEMBLY = 'Assembly',
  RECESS = 'Recess',
}

export enum EScheduleViewType {
  CLASS = 'Class',
  TEACHER = 'Teacher',
  STUDENT = 'Student',
  ROOM = 'Room',
}
